import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { estimateService } from '../services/estimateService';
import { salesOrderService } from '../services/salesOrderService';
import { 
  EstimateResponse, 
  EstimateStatus, 
  EstimateSendRequest, 
  EstimateConversionRequest 
} from '../types/estimate';


export interface EstimateDialogState {
  sendDialogOpen: boolean;
  convertDialogOpen: boolean;
  deleteDialogOpen: boolean;
}

export const useEstimateActions = (estimate: EstimateResponse | null) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dialog states
  const [dialogs, setDialogs] = useState<EstimateDialogState>({
    sendDialogOpen: false,
    convertDialogOpen: false,
    deleteDialogOpen: false
  });

  // Default form data
  const defaultSendData = useMemo<EstimateSendRequest>(() => ({
    send_via_email: true,
    send_via_sms: false,
    send_via_whatsapp: false,
    custom_message: ''
  }), []);

  const defaultConvertData = useMemo<EstimateConversionRequest>(() => ({
    order_date: format(new Date(), 'yyyy-MM-dd'),
    expected_delivery_date: '',
    shipping_charges: 0,
    packing_charges: 0,
    other_charges: [],
    notes: ''
  }), []);

  // Permission checks
  const permissions = useMemo(() => ({
    canEdit: estimate?.status === EstimateStatus.DRAFT,
    canSend: estimate?.status === EstimateStatus.DRAFT || estimate?.status === EstimateStatus.SENT,
    canConvert: estimate?.status === EstimateStatus.ACCEPTED && !estimate?.converted_to_order_id,
    canDelete: estimate?.status === EstimateStatus.DRAFT || estimate?.status === EstimateStatus.REJECTED,
    canAccept: estimate?.status === EstimateStatus.DRAFT || estimate?.status === EstimateStatus.SENT,
    canReject: estimate?.status === EstimateStatus.DRAFT || estimate?.status === EstimateStatus.SENT,
    canExpire: estimate?.status === EstimateStatus.DRAFT || estimate?.status === EstimateStatus.SENT
  }), [estimate?.status, estimate?.converted_to_order_id]);

  // Dialog management
  const openDialog = useCallback((dialogType: keyof EstimateDialogState) => {
    setDialogs(prev => ({ ...prev, [dialogType]: true }));
  }, []);

  const closeDialog = useCallback((dialogType: keyof EstimateDialogState) => {
    setDialogs(prev => ({ ...prev, [dialogType]: false }));
  }, []);

  const closeAllDialogs = useCallback(() => {
    setDialogs({
      sendDialogOpen: false,
      convertDialogOpen: false,
      deleteDialogOpen: false
    });
  }, []);

  // Action handlers
  const handleSendEstimate = useCallback(async (sendData: EstimateSendRequest) => {
    if (!estimate) return { success: false };

    try {
      setActionLoading(true);
      const result = await estimateService.sendEstimate(estimate.id, sendData);
      
      if (result.success) {
        enqueueSnackbar(result.message, { variant: 'success' });
        closeDialog('sendDialogOpen');
        return { success: true };
      } else {
        enqueueSnackbar(result.message, { variant: 'error' });
        return { success: false };
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to send estimate', { variant: 'error' });
      return { success: false };
    } finally {
      setActionLoading(false);
    }
  }, [estimate, enqueueSnackbar, closeDialog]);

  const handleConvertToSalesOrder = useCallback(async (convertData: EstimateConversionRequest) => {
    if (!estimate) return { success: false };

    try {
      setActionLoading(true);
      
      // Call the sales order service to convert estimate
      const result = await salesOrderService.convertEstimateToSalesOrder({
        estimate_id: estimate.id,
        order_date: convertData.order_date,
        expected_delivery_date: convertData.expected_delivery_date,
        shipping_charges: convertData.shipping_charges,
        packing_charges: convertData.packing_charges,
        other_charges: convertData.other_charges,
        notes: convertData.notes
      });
      
      enqueueSnackbar(result.message || 'Estimate converted to sales order successfully', { variant: 'success' });
      closeDialog('convertDialogOpen');
      
      // Navigate to the new sales order
      if (result.sales_order_id) {
        navigate(`/admin/documents/sales-orders/${result.sales_order_id}`);
      }
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to convert estimate';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return { success: false };
    } finally {
      setActionLoading(false);
    }
  }, [estimate, enqueueSnackbar, closeDialog, navigate]);

  const handleDeleteEstimate = useCallback(async () => {
    if (!estimate) return { success: false };

    try {
      setActionLoading(true);
      await estimateService.deleteEstimate(estimate.id);
      enqueueSnackbar('Estimate deleted successfully', { variant: 'success' });
      navigate('/admin/documents/estimates');
      return { success: true };
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to delete estimate', { variant: 'error' });
      return { success: false };
    } finally {
      setActionLoading(false);
      closeDialog('deleteDialogOpen');
    }
  }, [estimate, enqueueSnackbar, navigate, closeDialog]);

  const handleDownloadPDF = useCallback(async () => {
    if (!estimate) return { success: false };

    try {
      setActionLoading(true);
      const blob = await estimateService.generatePDF(estimate.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estimate-${estimate.estimate_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
      return { success: true };
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to download PDF', { variant: 'error' });
      return { success: false };
    } finally {
      setActionLoading(false);
    }
  }, [estimate, enqueueSnackbar]);

  const handleEditEstimate = useCallback(() => {
    if (!estimate) return;
    navigate(`/admin/documents/estimates/${estimate.id}/edit`);
  }, [estimate, navigate]);

  const handleAcceptEstimate = useCallback(async () => {
    if (!estimate) return { success: false };

    try {
      setActionLoading(true);
      await estimateService.updateStatus(estimate.id, {
        status: EstimateStatus.ACCEPTED,
        notes: 'Estimate accepted by customer'
      });
      enqueueSnackbar('Estimate marked as accepted', { variant: 'success' });
      // Trigger a refresh of the estimate data
      window.location.reload();
      return { success: true };
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to accept estimate', { variant: 'error' });
      return { success: false };
    } finally {
      setActionLoading(false);
    }
  }, [estimate, enqueueSnackbar]);

  const handleRejectEstimate = useCallback(async () => {
    if (!estimate) return { success: false };

    try {
      setActionLoading(true);
      await estimateService.updateStatus(estimate.id, {
        status: EstimateStatus.REJECTED,
        notes: 'Estimate rejected by customer'
      });
      enqueueSnackbar('Estimate marked as rejected', { variant: 'success' });
      // Trigger a refresh of the estimate data
      window.location.reload();
      return { success: true };
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to reject estimate', { variant: 'error' });
      return { success: false };
    } finally {
      setActionLoading(false);
    }
  }, [estimate, enqueueSnackbar]);

  const handleExpireEstimate = useCallback(async () => {
    if (!estimate) return { success: false };

    try {
      setActionLoading(true);
      await estimateService.updateStatus(estimate.id, {
        status: EstimateStatus.EXPIRED,
        notes: 'Estimate marked as expired'
      });
      enqueueSnackbar('Estimate marked as expired', { variant: 'success' });
      // Trigger a refresh of the estimate data
      window.location.reload();
      return { success: true };
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to expire estimate', { variant: 'error' });
      return { success: false };
    } finally {
      setActionLoading(false);
    }
  }, [estimate, enqueueSnackbar]);

  return {
    // State
    actionLoading,
    dialogs,
    permissions,
    defaultSendData,
    defaultConvertData,
    
    // Dialog management
    openDialog,
    closeDialog,
    closeAllDialogs,
    
    // Actions
    handleSendEstimate,
    handleConvertToSalesOrder,
    handleDeleteEstimate,
    handleDownloadPDF,
    handleEditEstimate,
    handleAcceptEstimate,
    handleRejectEstimate,
    handleExpireEstimate
  };
};